import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5a91ec1d-c247-4280-a3f0-08c1958626f0/artifacts/3z2nenay_BNPH%20Logo%20with%2050th%20banner.png";

const leftNavItems = [
  {
    name: 'Our Company',
    path: '/our-company',
    dropdown: [
      { name: 'About Us', path: '/our-company' },
      { name: 'Blog', path: '/blog' },
    ]
  },
  { name: 'Project Gallery', path: '/project-gallery' },
  { 
    name: 'Services', 
    path: '/services',
    dropdown: [
      { name: 'Residential', path: '/services/residential' },
      { name: 'Commercial', path: '/services/commercial' },
      { name: 'Boilers', path: '/services/boilers' },
      { name: 'Water Heaters', path: '/services/water-heaters' },
      { name: 'Air Conditioning', path: '/services/air-conditioning' },
    ]
  },
];

const rightNavItems = [
  { name: 'Careers', path: '/careers' },
  { name: 'Contact Us', path: '/contact' },
];

const allNavItems = [...leftNavItems, ...rightNavItems];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path || location.pathname === '/';

  const NavItem = ({ item }) => (
    <div 
      className="relative group"
      onMouseEnter={() => item.dropdown && setOpenDropdown(item.name)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <Link
        to={item.path}
        className={`px-4 py-6 font-oswald font-semibold text-sm uppercase tracking-wider transition-colors flex items-center gap-1 ${
          location.pathname === item.path ? 'text-[#F58220]' : 'text-[#14366C] hover:text-[#F58220]'
        }`}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
      >
        {item.name}
        {item.dropdown && <ChevronDown className="w-4 h-4" />}
      </Link>
      
      {/* Dropdown */}
      {item.dropdown && openDropdown === item.name && (
        <div className="absolute top-full left-0 bg-white shadow-lg min-w-[200px] py-2 z-50 border-t-2 border-[#F58220]">
          {item.dropdown.map((subItem) => (
            <Link
              key={subItem.name}
              to={subItem.path}
              onClick={() => setOpenDropdown(null)}
              className="block px-4 py-2 text-sm text-[#14366C] hover:bg-[#F58220] hover:text-white transition-colors"
              data-testid={`nav-sub-${subItem.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              {subItem.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header className="bg-white shadow-md sticky top-0 z-50" data-testid="header">
      {/* Desktop Header - Logo Centered and Hanging Over */}
      <div className="hidden md:block relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Left Navigation */}
            <nav className="flex items-center flex-1 justify-end pr-8">
              {leftNavItems.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </nav>

            {/* Center Logo - Hanging Over Effect */}
            <Link to="/" className="relative z-20 flex-shrink-0" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="BNPH - Bruce Nelson Plumbing & Heating - 50th Anniversary" 
                className="h-32 lg:h-40 w-auto"
              />
            </Link>

            {/* Right Navigation */}
            <nav className="flex items-center flex-1 justify-start pl-8">
              {rightNavItems.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
              {/* Phone Number */}
              <a 
                href="tel:651-738-9354" 
                className="ml-4 flex items-center gap-2 bg-[#14366C] border-2 border-[#14366C] rounded-lg px-4 py-2 hover:bg-[#F58220] hover:border-[#F58220] transition-colors group"
              >
                <span className="font-oswald text-lg font-bold text-white group-hover:text-white">
                  651-738-9354
                </span>
                <Phone className="w-5 h-5 text-white group-hover:text-white" />
              </a>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden">
        <div className="px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link-mobile">
            <img 
              src={LOGO_URL} 
              alt="BNPH - Bruce Nelson Plumbing & Heating - 50th Anniversary" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3">
            <a href="tel:651-738-9354" className="text-[#14366C]">
              <Phone className="w-6 h-6" />
            </a>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-[#14366C]"
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200 py-4 px-4">
            <Link
              to="/"
              className={`block py-3 font-oswald font-semibold uppercase tracking-wide ${
                location.pathname === '/' ? 'text-[#F58220]' : 'text-[#14366C]'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            {allNavItems.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.path}
                  className={`block py-3 font-oswald font-semibold uppercase tracking-wide ${
                    location.pathname === item.path ? 'text-[#F58220]' : 'text-[#14366C]'
                  }`}
                  onClick={() => !item.dropdown && setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
                {item.dropdown && (
                  <div className="pl-4 border-l-2 border-[#F58220] ml-2">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.path}
                        className="block py-2 text-sm text-gray-600 hover:text-[#F58220]"
                        data-testid={`mobile-nav-sub-${subItem.name.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
